import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  PageLayout,
  SearchType,
  SliderType,
  NextPageService,
} from "@cvx/nextpage";
import { appInfo, environment } from "src/environments/environment";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthError, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { HttpErrorResponse } from "@angular/common/http";
import { StorageService } from "./core/services/storage.service";
import { UserService } from "./core/http/user.service";
import { UniqueVisitEventModel } from "./shared/models/app-insights";
import { ApplicationInsightsService } from "./core/http/application-insights.service";
import { Subject, filter, firstValueFrom, takeUntil } from "rxjs";
import { Router, RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { HeaderComponent } from "./modules/home/components/header/header.component";
import { MenubarComponent } from "./modules/home/components/menubar/menubar.component";
import { SuccessToastComponent } from "./shared/components/success-toast/success-toast.component";
import { ErrorToastComponent } from "./shared/components/error-toast/error-toast.component";
import { FooterComponent } from "./modules/home/components/footer/footer.component";
import { ChatbotIconComponent } from "./modules/home/components/chatbot-icon/chatbot-icon.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    HeaderComponent,
    MenubarComponent,
    SuccessToastComponent,
    ErrorToastComponent,
    FooterComponent,
    ChatbotIconComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = "ELL";
  name = "";
  loggedIn: boolean;
  isAuthenticated: boolean = false;
  contentContactName = appInfo.contentContactName;
  contentContactUrl = appInfo.contentContactUrl;
  technicalContactName = appInfo.technicalContactName;
  technicalContactUrl = appInfo.technicalContactUrl;
  copyright = appInfo.copyright;
  lastUpdate = environment.type == "DEV" ? "" : appInfo.lastUpdate;

  PageLayout = PageLayout;
  SearchType = SearchType;
  SliderType = SliderType;

  public productUserGuide = appInfo.productUserGuide;
  public productUserGuideUrl = appInfo.productUserGuideUrl;
  initializationError = "";
  private readonly _destroying$ = new Subject<void>();
  private userProfileInitialization: Promise<void> | null = null;

  constructor(
    private NP: NextPageService,
    private authService: MsalService,
    private storageService: StorageService,
    private userService: UserService,
    private appInsights: ApplicationInsightsService,
    public router: Router,
    private msalBroadcast: MsalBroadcastService
  ) {
    this.NP.options.sitename = "Enterprise Lessons Learned";
    this.NP.options.navigationItems = [
      {
        title: "dashboard",
        url: "/home",
      },
      {
        title: "create new lesson",
        url: "/lessons",
      },
      {
        title: "search",
        url: "/lessons",
      },
      {
        title: "my actions",
        url: "/lessons",
      },
      {
        title: "reporting & metrics",
        url: "/report",
      },
      {
        title: "feedback",
        url: "/report",
      },
    ];
  }

  ngOnInit() {
    this.msalBroadcast.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((_) => this.setLoginDisplay());
  }

  setLoginDisplay() {
    this.isAuthenticated =
      this.authService.instance.getAllAccounts().length > 0;
    if (this.isAuthenticated && this.router.url.endsWith("/login-failed")) {
      this.router.navigate(["/"]).then((_) => {});
    } else if (this.isAuthenticated) {
      this.setUserProfile();
    }
  }

  public ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  public setUserProfile(): void {
    if (this.loggedIn || this.userProfileInitialization !== null) {
      return;
    }

    this.initializationError = "";
    this.userProfileInitialization = this.initializeUserProfile().finally(() => {
      this.userProfileInitialization = null;
    });
  }

  public retryUserProfileInitialization(): void {
    this.initializationError = "";
    this.setUserProfile();
  }

  private async initializeUserProfile(): Promise<void> {
    let step = "account selection";
    try {
      this.userService.setAccount();
      const currentUserProfile = this.userService.getCurrentUser();
      if (currentUserProfile === null) {
        throw new Error("No authenticated account is available to initialize the user profile.");
      }

      step = "token refresh";
      const token = await firstValueFrom(this.userService.refreshApiAccessToken());
      step = "profile request";
      const response = await firstValueFrom(this.userService.loggedUser(token.accessToken));

      sessionStorage.setItem("profile", JSON.stringify(currentUserProfile));
      sessionStorage.setItem("name", currentUserProfile.name);
      sessionStorage.setItem("email", currentUserProfile.username);
      sessionStorage.setItem("uniqueKey", currentUserProfile.username.toLowerCase());
      sessionStorage.setItem("profile-ell", JSON.stringify(response));
      sessionStorage.setItem("businessunit", response.businessUnit ?? "");
      sessionStorage.setItem("roles", response.roleClaims?.join(",") ?? "");
      step = "configuration request";
      await this.storageService.setConfigDataInLocalStorageStorage();
      this.loggedIn = true;

      let uniqueVisitModel = new UniqueVisitEventModel();
      uniqueVisitModel.userFullName = response.fullName;
      uniqueVisitModel.userBusinessUnit = response.businessUnit;
      uniqueVisitModel.userUniqueKey = response.uniqueKey;
      this.appInsights.createUniqueVisitCustomEvent(uniqueVisitModel).subscribe();
    } catch (caughtError) {
      let error = caughtError;
      if (step === "token refresh" && error instanceof InteractionRequiredAuthError) {
        try {
          await firstValueFrom(this.authService.acquireTokenRedirect({
            account: this.userService.getCurrentUser() ?? undefined,
            scopes: [`${environment.appRegistration.clientId}/user_impersonation`],
          }));
          return;
        } catch (redirectError) {
          error = redirectError;
        }
      }

      console.error("ELL initialization failed", {
        step,
        status: error instanceof HttpErrorResponse ? error.status : undefined,
        code: error instanceof AuthError ? error.errorCode : undefined,
      });
      this.initializationError = step === "configuration request"
        ? "ELL configuration could not be loaded. Please try again. If the issue continues, contact support."
        : "Your ELL profile could not be initialized. Please try again. If the issue continues, contact support.";
    }
  }
}
